import { Close } from '@mui/icons-material';
import {
    Box,
    Divider,
    IconButton,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import DatasetHeader from '~/components/DatasetHeader';
import Sidebar from '~/components/Sidebar';
import { DICOMApprovalStatus, Roi } from '~/domain/dicom/dicomstructureset';
import { Modality } from '~/domain/dicom/modality';
import { StructureSet } from '~/domain/patient/structureset';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { reviewToolState } from '~/state/atoms/toolsAtoms';
import ReviewStructure from './ReviewStructure';
import ApprovalStatusDropdown from '../explorer-sidebar/Structures/StructureSet/StructureSetDetails/StructureSetSettings/ApprovalStatusDropdown';

export default function ReviewStructureSet() {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const reviewToolOpen = useRecoilValue(reviewToolState);
    const setReviewToolState = useSetRecoilState(reviewToolState);
    const selectedROI = useRecoilValue(selectedROIState);

    const selectedStructure = currentStructureSet?.roiArray?.find(
        (roi) => roi?.roiNumber === selectedROI
    );

    return (
        <Sidebar open={!!reviewToolOpen} side="right">
            <Toolbar variant="dense" />
            <Box py={2} px={2}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h6">
                        <FormattedMessage id="review" />
                    </Typography>
                    <IconButton
                        aria-label="Close"
                        size="small"
                        onClick={() => setReviewToolState(false)}
                    >
                        <Close />
                    </IconButton>
                </Stack>
                {currentStructureSet && selectedStructure ? (
                    <ReviewTool
                        currentStructureSet={currentStructureSet}
                        selectedStructure={selectedStructure}
                    />
                ) : (
                    <Typography variant="body2">
                        No structure selected.
                        <br />
                        In order to start reviewing select one structure from
                        the list.
                    </Typography>
                )}
            </Box>
        </Sidebar>
    );
}

const ReviewTool = ({
    selectedStructure,
}: {
    currentStructureSet: StructureSet;
    selectedStructure: Roi;
}) => {
    const {
        structureSet,
        setStructureSetApprovalStatus,
        setRoisApprovalStatus,
    } = useStructureSet();

    const handleApprovalChange = (newApprovalStatus: DICOMApprovalStatus) => {
        setStructureSetApprovalStatus(newApprovalStatus);
        setRoisApprovalStatus(newApprovalStatus);
    };

    return (
        <>
            <Stack mb={1}>
                <DatasetHeader
                    label={<FormattedMessage id="structureSet" />}
                    modality={Modality.RTSTRUCT}
                />
                <Stack direction="row" justifyContent="space-between" mb={2}>
                    <Typography variant="body2">
                        {structureSet?.structureSetLabel}
                    </Typography>
                </Stack>
                <ApprovalStatusDropdown
                    structureSet={structureSet}
                    onApprovalStatusChange={handleApprovalChange}
                />
                <Divider orientation='horizontal' sx={{ color: 'white', my: 2 }} />
            </Stack>
            <ReviewStructure structure={selectedStructure} />
        </>
    );
};